/**
 * The browser's own database (IndexedDB) on this device: where pictures and
 * cards are kept. Nothing in it ever goes to the server.
 *
 * One database per account, "fmc-<user id>", so two people who sign in on the
 * same browser never see each other's cards. Opened when the account is known
 * (main.tsx); every read and write waits for that.
 *
 *   pictures   one row per picture: its name and sizes (no pixels)
 *   files      the picture files themselves (Blobs), keyed "<picture id>/<kind>"
 *   projects   one row per card
 *   versions   each version of a card, with its document; index "project"
 *   meta       small things kept with them, by name: the card folder (folder.ts),
 *              when the last backup was made (version 2, 2026-10-10)
 *   events     the events the cards invite to: their details and guests' names, which
 *              stay here and in the card files, never on the server (version 3, 2026-10-24;
 *              events.ts)
 */
const VERSION = 3;
export type StoreName = 'pictures' | 'files' | 'projects' | 'versions' | 'meta' | 'events';

let opened: { userId: string; db: Promise<IDBDatabase> } | null = null;
/** Settles once an account's database is open: what every read and write waits on. */
let ready: (db: Promise<IDBDatabase>) => void;
let waiting = new Promise<IDBDatabase>((r) => { ready = r; });
const listeners = new Set<() => void>();

export const dbName = (userId: string) => `fmc-${userId}`;

function open(userId: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(dbName(userId), VERSION);
    r.onupgradeneeded = (e) => {
      const db = r.result;
      if (e.oldVersion < 1) {
        db.createObjectStore('pictures', { keyPath: 'id' });
        db.createObjectStore('files');
        db.createObjectStore('projects', { keyPath: 'id' });
        db.createObjectStore('versions', { keyPath: 'id' }).createIndex('project', 'projectId');
      }
      if (e.oldVersion < 2) db.createObjectStore('meta');
      if (e.oldVersion < 3) db.createObjectStore('events', { keyPath: 'id' });
    };
    r.onsuccess = () => {
      // another tab deleting this account's store (account deleted) or opening a newer version: let it
      r.result.onversionchange = () => r.result.close();
      resolve(r.result);
    };
    r.onerror = () => reject(r.error ?? new Error('This browser would not open its storage for FoldMyCard.'));
    r.onblocked = () => reject(new Error('FoldMyCard is open in another tab with an older version. Close it and try again.'));
  });
}

/** Use this account's database from now on (null: signed out). */
export function openAccountDb(userId: string | null) {
  if ((opened?.userId ?? null) === userId) return;
  if (opened) void opened.db.then((db) => db.close()).catch(() => {});
  if (!userId) {
    opened = null;
    waiting = new Promise((r) => { ready = r; });
  } else {
    opened = { userId, db: open(userId) };
    ready(opened.db);
    waiting = Promise.resolve(opened.db);
  }
  for (const l of listeners) l();
}
/** Told whenever the account changes, so cached picture addresses can be let go (store.ts). */
export const onAccountChange = (f: () => void) => listeners.add(f);

const db = () => waiting;

/** A request's result, as a promise. Awaiting these inside a transaction keeps it alive. */
export function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/**
 * Run `work` in one transaction; resolves with its result once everything in it
 * is written. Inside, await only requests on `t` - awaiting anything else lets
 * the transaction finish early.
 */
export async function tx<T>(stores: StoreName[], mode: IDBTransactionMode, work: (t: IDBTransaction) => Promise<T>): Promise<T> {
  const t = (await db()).transaction(stores, mode);
  const finished = new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error ?? new Error('The change was not saved.'));
  });
  let result: T;
  try {
    result = await work(t);
  } catch (err) {
    try { t.abort(); } catch { /* already finished */ }
    await finished.catch(() => {});
    throw err;
  }
  await finished;
  return result;
}

/** A small value kept with this account's cards (the "meta" store); undefined when there is none. */
export const getMeta = <T>(key: string) =>
  tx(['meta'], 'readonly', (t) => req(t.objectStore('meta').get(key) as IDBRequest<T | undefined>));
export const setMeta = (key: string, value: unknown) =>
  tx(['meta'], 'readwrite', async (t) => { await req(t.objectStore('meta').put(value, key)); });
export const deleteMeta = (key: string) =>
  tx(['meta'], 'readwrite', async (t) => { await req(t.objectStore('meta').delete(key)); });

/** Delete one account's database on this device (after the account itself is deleted). */
export function deleteAccountDb(userId: string): Promise<void> {
  if (opened?.userId === userId) openAccountDb(null);
  return new Promise((resolve) => {
    const r = indexedDB.deleteDatabase(dbName(userId));
    r.onsuccess = r.onerror = r.onblocked = () => resolve();
  });
}

/**
 * Ask the browser to keep this site's storage even when the device runs low
 * (asked once, when the first picture is added). Without it, a browser may
 * clear the pictures of a site that is not used for a while.
 */
export async function keepStorage(): Promise<boolean> {
  try {
    if (await navigator.storage?.persisted?.()) return true;
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
